<template>
    <v-container grid-list-md pa-0 relative>
        <v-layout row wrap>
            <v-flex xs12>
                <v-card outlined class="primary--border">
                    <v-card-title class="title">
                        <span>Database Backups <br>
                            <small class="grey--text">
                                Backs up the entire billing database. Limited to one successful backup
                                every 24 hours.
                            </small>
                        </span>
                        <v-spacer />
                        <v-btn icon :loading="loading" :disabled="taking" @click="search" class="mr-2" title="Refresh">
                            <v-icon>refresh</v-icon>
                        </v-btn>

                        <search-button :disabled="taking || !!cooldownReason" icon="backup"
                            permission="database-backup-read" @action="takeBackup">
                            {{ taking ? "Backing Up..." : "Take Backup Now" }}
                        </search-button>
                    </v-card-title>

                    <v-card-text v-if="alert.message" class="pt-0">
                        <v-alert :type="alert.type" dense outlined dismissible :value="true"
                            @input="alert.message = ''">
                            {{ alert.message }}
                        </v-alert>
                    </v-card-text>

                    <v-card-text v-else-if="cooldownReason" class="pt-0">
                        <v-alert type="info" dense outlined icon="schedule" :value="true">
                            {{ cooldownReason }}
                        </v-alert>
                    </v-card-text>



                    <v-data-table :headers="headers" :items="records" :options.sync="pagination" :footer-props="{
                        ...footerProps,
                        itemsPerPageOptions: [10, 25, 50]
                    }" :server-items-length="totalItems" :loading="loading" item-key="id"
                        class="database-backup-table">
                        <template v-slot:item="{ index, item }">
                            <tr>
                                <td class="text-center">
                                    {{ rowNumber(index) }}
                                </td>

                                <td class="text-left">
                                    {{ item.filename }}
                                </td>

                                <td class="text-center">
                                    <v-chip x-small label :color="statusColor(item.status)" text-color="white">
                                        <v-progress-circular v-if="item.status === 'in_progress'" indeterminate
                                            size="10" width="2" color="white" class="mr-1" />
                                        {{ statusLabel(item.status) }}
                                    </v-chip>
                                </td>

                                <td class="text-right">
                                    {{ formatBytes(item.size_bytes) }}
                                </td>

                                <td class="text-left">
                                    {{ item.triggered_by || "-" }}
                                </td>

                                <td class="text-left">
                                    {{ formatDate(item.started_at) }}
                                </td>

                                <td class="text-left">
                                    {{ formatDate(item.completed_at) }}
                                </td>

                                <td class="text-center">
                                    {{ duration(item.started_at, item.completed_at) }}
                                </td>

                                <td class="text-center">
                                    <v-btn v-if="item.status === 'success'" small text color="primary"
                                        :loading="downloadingId === item.id" :disabled="!!downloadingId"
                                        @click="download(item)">
                                        <v-icon left small>cloud_download</v-icon>
                                        Download
                                    </v-btn>

                                    <v-tooltip v-else-if="item.status === 'failed'" top max-width="320">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on" class="error--text">
                                                <v-icon color="error" small>error_outline</v-icon>
                                                Failed
                                            </span>
                                        </template>
                                        <span>{{ item.error_message || "Unknown error" }}</span>
                                    </v-tooltip>

                                    <span v-else class="grey--text">Running…</span>
                                </td>
                            </tr>
                        </template>

                        <template v-slot:no-data>
                            <div class="pa-8 text-center grey--text">
                                No backups yet. Click "Take Backup Now" to create the first one.
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import Mixins from "@/library/Mixins";
import { v2Services } from "@/library/helpers";

export default {
    name: "DatabaseBackup",

    mixins: [Mixins],

    data: () => ({
        records: [],
        loading: false,
        taking: false,
        downloadingId: null,
        requestInitialized: false,

        totalItems: 0,

        alert: {
            type: "success",
            message: "",
        },

        pagination: {
            page: 1,
            itemsPerPage: 10,
            sortBy: [],
            sortDesc: [],
        },

        headers: [
            {
                text: "#",
                align: "center",
                value: "id",
                width: 60,
                sortable: false,
            },
            {
                text: "Filename",
                align: "left",
                value: "filename",
                sortable: false,
            },
            {
                text: "Status",
                align: "center",
                value: "status",
                width: 130,
                sortable: false,
            },
            {
                text: "Size",
                align: "right",
                value: "size_bytes",
                width: 110,
                sortable: false,
            },
            {
                text: "Triggered By",
                align: "left",
                value: "triggered_by",
                width: 160,
                sortable: false,
            },
            {
                text: "Started",
                align: "left",
                value: "started_at",
                width: 180,
                sortable: false,
            },
            {
                text: "Completed",
                align: "left",
                value: "completed_at",
                width: 180,
                sortable: false,
            },
            {
                text: "Duration",
                align: "center",
                value: "duration",
                width: 100,
                sortable: false,
            },
            {
                text: "Actions",
                align: "center",
                value: "actions",
                width: 140,
                sortable: false,
            },
        ],
    }),

    computed: {
        // Only meaningful on page 1 (sorted newest-first) since that's the
        // only page guaranteed to hold the most recent backup record.
        cooldownReason() {
            if (this.pagination.page !== 1 || !this.records.length) {
                return "";
            }

            const latest = this.records[0];

            if (latest.status === "in_progress") {
                return "A backup is currently running.";
            }

            if (latest.status === "success" && latest.completed_at) {
                const completedAt = new Date(latest.completed_at);
                const nextAllowed = new Date(
                    completedAt.getTime() + 24 * 60 * 60 * 1000
                );

                if (nextAllowed.getTime() > Date.now()) {
                    return `Daily limit reached. Next backup available at ${this.formatDate(
                        nextAllowed.toISOString()
                    )}.`;
                }
            }

            return "";
        },
    },

    watch: {
        pagination: {
            deep: true,
            handler(newValue, oldValue) {
                if (!this.requestInitialized) {
                    this.requestInitialized = true;
                    this.get();
                    return;
                }

                if (!oldValue) {
                    return;
                }

                const pageChanged = newValue.page !== oldValue.page;
                const perPageChanged =
                    newValue.itemsPerPage !== oldValue.itemsPerPage;

                if (pageChanged || perPageChanged) {
                    this.get();
                }
            },
        },
    },

    methods: {
        search() {
            if (this.pagination.page !== 1) {
                this.pagination.page = 1;
                return;
            }

            this.get();
        },

        get() {
            const params = new URLSearchParams();
            params.append("page", this.pagination.page || 1);
            params.append("perPage", this.pagination.itemsPerPage || 10);

            this.loading = true;

            this.$restV2(v2Services.BILLING)
                .get(`/v2/database-backups?${params.toString()}`)
                .then((response) => {
                    const payload = response.data || {};
                    const records = Array.isArray(payload.data)
                        ? payload.data
                        : [];

                    this.records = records;
                    this.totalItems = payload.meta
                        ? Number(payload.meta.total || 0)
                        : records.length;
                })
                .catch(() => {
                    this.records = [];
                    this.totalItems = 0;
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        takeBackup() {
            if (this.taking || this.cooldownReason) {
                return;
            }

            this.taking = true;
            this.alert = {
                type: "info",
                message:
                    "Backup in progress — this can take a few minutes for larger databases. Please keep this page open.",
            };

            this.$restV2(v2Services.BILLING)
                .post("/v2/database-backups")
                .then((response) => {
                    const data = (response.data && response.data.data) || {};

                    this.alert = {
                        type: "success",
                        message: `Backup completed successfully (${this.formatBytes(
                            data.size_bytes
                        )}). Saved as ${data.filename}.`,
                    };

                    this.search();
                })
                .catch((error) => {
                    const message =
                        (error.response &&
                            error.response.data &&
                            error.response.data.message) ||
                        "Failed to take database backup.";

                    this.alert = { type: "error", message };
                    this.get();
                })
                .finally(() => {
                    this.taking = false;
                });
        },

        download(item) {
            if (this.downloadingId) {
                return;
            }

            this.downloadingId = item.id;

            this.$restV2(v2Services.BILLING)
                .get(`/v2/database-backups/${item.id}/download`)
                .then((response) => {
                    const url =
                        response.data &&
                        response.data.data &&
                        response.data.data.url;

                    if (url) {
                        window.open(url, "_blank");
                    }
                })
                .catch((error) => {
                    const message =
                        (error.response &&
                            error.response.data &&
                            error.response.data.message) ||
                        "Failed to generate the download link.";

                    this.alert = { type: "error", message };
                })
                .finally(() => {
                    this.downloadingId = null;
                });
        },

        formatBytes(bytes) {
            const value = Number(bytes);

            if (!value || Number.isNaN(value)) {
                return "-";
            }

            const units = ["B", "KB", "MB", "GB", "TB"];
            let size = value;
            let unitIndex = 0;

            while (size >= 1024 && unitIndex < units.length - 1) {
                size /= 1024;
                unitIndex += 1;
            }

            return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
        },

        formatDate(value) {
            if (!value) {
                return "-";
            }

            const date = new Date(value);

            if (Number.isNaN(date.getTime())) {
                return "-";
            }

            return date.toLocaleString();
        },

        duration(startedAt, completedAt) {
            if (!startedAt || !completedAt) {
                return "-";
            }

            const start = new Date(startedAt).getTime();
            const end = new Date(completedAt).getTime();

            if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
                return "-";
            }

            const totalSeconds = Math.round((end - start) / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;

            return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
        },

        statusLabel(status) {
            if (status === "success") return "Success";
            if (status === "failed") return "Failed";
            if (status === "in_progress") return "Running";
            return status || "-";
        },

        statusColor(status) {
            if (status === "success") return "success";
            if (status === "failed") return "error";
            if (status === "in_progress") return "info";
            return "grey";
        },

        rowNumber(index) {
            const page = this.pagination.page || 1;
            const perPage = this.pagination.itemsPerPage || 10;
            return (page - 1) * perPage + index + 1;
        },
    },
};
</script>

<style lang="scss" scoped>
.database-backup-table ::v-deep th {
    white-space: nowrap;
    font-weight: 700;
}

.database-backup-table ::v-deep td {
    white-space: nowrap;
}
</style>
